/**
 * 市场项目数据加载与筛选 Hook
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { fetchAllMarketProjects } from '../api/market';
import {
  DEFAULT_PROJECTS_DATA_FILTER,
  type MarketFilterOptions,
  type MarketProject,
  type MarketProjectStatistics,
  type ProjectsDataFilter,
} from '../types/market';
import {
  collectFilterOptionsForCategory,
  countProjectsByCategory,
  filterMarketProjects,
} from '../utils/marketHelpers';

export interface UseMarketProjectsResult {
  projects: MarketProject[];
  allProjects: MarketProject[];
  loading: boolean;
  error: string | null;
  filter: ProjectsDataFilter;
  setFilter: Dispatch<SetStateAction<ProjectsDataFilter>>;
  statistics: MarketProjectStatistics;
  filterOptions: MarketFilterOptions;
}

export function useMarketProjects(): UseMarketProjectsResult {
  const [allProjects, setAllProjects] = useState<MarketProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ProjectsDataFilter>(
    DEFAULT_PROJECTS_DATA_FILTER,
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchAllMarketProjects();
      setAllProjects(list);
    } catch (e) {
      const message =
        e instanceof Error ? e.message : '市场项目数据加载失败，请稍后重试';
      setError(message);
      setAllProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const projects = useMemo(
    () => filterMarketProjects(allProjects, filter),
    [allProjects, filter],
  );

  const statistics = useMemo(
    () => countProjectsByCategory(projects),
    [projects],
  );

  const filterOptions = useMemo(
    () => collectFilterOptionsForCategory(allProjects, filter.category),
    [allProjects, filter.category],
  );

  return {
    projects,
    allProjects,
    loading,
    error,
    filter,
    setFilter,
    statistics,
    filterOptions,
  };
}
